import type { ReactNode } from "react"
import HoverCard from "@/v2-ui/hoverCard"
import type { IconProps } from "@/v2-ui/icon"
import cn from "@/v2-ui/utils/utils.cn"
import Badge, {
  type BadgeSize,
  type BadgeVariant,
} from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"

export type BadgeListItem = {
  /** Stable identity; the label when omitted. */
  key?: string
  label: string
  /** Where the name leads (the table cell links' partial-href form, e.g. "?v=plan:3"). */
  to?: string
  variant?: BadgeVariant
  /** Extra classes on the inline pill — a product's tone (license.product.utils). */
  className?: string
  /** Glyph before the name on the inline pill. */
  iconSrc?: IconProps["src"]
  /** A word beside the name in the overflow card: "add-on", "4 features". */
  hint?: string
  /** Hover on the inline pill. */
  tooltip?: ReactNode
}

export type BadgeListProps = {
  items: readonly BadgeListItem[]
  /**
   * The most pills shown inline before the rest fold into one "+N" pill;
   * Infinity shows them all. Fewer show when the names are long (see
   * `charBudget`), so a pill is read whole rather than cut to "Placepoint B…".
   */
  max?: number
  /** Characters of names shown inline before the rest fold; the first name always shows. */
  charBudget?: number
  /**
   * Let the pills wrap onto more lines instead of truncating. For a detail
   * view with room, never for a table cell: a row is read by its height.
   */
  isWrapped?: boolean
  size?: BadgeSize
  /** The pills' default hue; an item's own wins. */
  variant?: BadgeVariant
  /** What stands in the cell when there is nothing to list. */
  emptyLabel?: ReactNode
  /** The overflow card's heading, with the count after it: "Included in · 11". */
  overflowTitle?: string
  className?: string
}

/** Pills shown inline by default: enough to recognise a row, never enough to wrap it. */
const DEFAULT_MAX = 3
/**
 * Roughly what a 200 px cell holds at the pills' size. Two short names
 * ("Basic", "Standard") show side by side; one long name ("Placepoint
 * Enterprise") stands alone, and the rest fold into the count.
 */
const DEFAULT_CHAR_BUDGET = 24

/** How many leading items show inline: at least one, at most `max`, within the budget. */
function countShown(
  items: readonly BadgeListItem[],
  max: number,
  charBudget: number,
) {
  let used = 0
  let count = 0
  for (const item of items) {
    if (count >= max) break
    used += item.label.length
    if (count > 0 && used > charBudget) break
    count += 1
  }
  return count
}

function stopRowClick(event: React.MouseEvent) {
  event.stopPropagation()
}

/**
 * A list of named things in one table cell, one line tall whatever the
 * count: the first pills inline (each a link when it has somewhere to
 * go, truncating rather than wrapping), then a "+N" pill whose hover card
 * lists every item, names linked, with its hint. Rows that carried ten
 * pills used to grow to ten lines; a board is read by scanning rows of the
 * same height, and the full list is one hover away.
 */
function BadgeList(props: BadgeListProps) {
  const {
    items,
    max = DEFAULT_MAX,
    charBudget = DEFAULT_CHAR_BUDGET,
    isWrapped = false,
    size = "xs",
    variant = "neutral",
    emptyLabel = null,
    overflowTitle,
    className,
  } = props

  if (items.length === 0) {
    return emptyLabel === null ? null : <>{emptyLabel}</>
  }

  // A wrapped list has room for every pill the caller allows; an inline
  // one shows what reads whole and folds the rest.
  const shown = items.slice(
    0,
    isWrapped ? max : countShown(items, max, charBudget),
  )
  const hiddenCount = items.length - shown.length

  return (
    <span
      className={cn(
        "badge-list",
        isWrapped && "badge-list-is-wrapped",
        className,
      )}
    >
      {shown.map((item) => {
        const pill = (
          <Badge
            size={size}
            variant={item.variant ?? variant}
            iconSrc={item.iconSrc}
            tooltip={item.tooltip}
            className={cn("badge-list-pill", item.className)}
          >
            <span className="badge-list-pill-label">{item.label}</span>
          </Badge>
        )
        const key = item.key ?? item.label
        if (!item.to)
          return (
            <span key={key} className="badge-list-pill-wrap">
              {pill}
            </span>
          )
        return (
          <Link
            key={key}
            to={item.to}
            variant="unstyled"
            iconSrc={null}
            className="badge-list-pill-wrap badge-list-pill-link"
            title={`Open ${item.label}`}
            onClick={stopRowClick}
          >
            {pill}
          </Link>
        )
      })}

      {hiddenCount > 0 && (
        <HoverCard openDelay={200} closeDelay={120}>
          <HoverCard.Trigger asChild>
            <button
              type="button"
              className="badge-list-more"
              aria-label={`${hiddenCount} more`}
              onClick={stopRowClick}
            >
              <Badge size={size} variant="neutral">
                +{hiddenCount}
              </Badge>
            </button>
          </HoverCard.Trigger>
          <HoverCard.Content
            side="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            className="badge-list-card"
          >
            {overflowTitle && (
              <p className="badge-list-card-title">
                {overflowTitle} · {items.length}
              </p>
            )}
            <ul className="badge-list-card-list">
              {items.map((item) => (
                <li
                  key={item.key ?? item.label}
                  className="badge-list-card-item"
                >
                  {item.to ? (
                    <Link
                      to={item.to}
                      variant="muted"
                      size="xs"
                      iconSrc={null}
                      className="badge-list-card-name"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span className="badge-list-card-name">{item.label}</span>
                  )}
                  {item.hint && (
                    <span className="badge-list-card-hint">{item.hint}</span>
                  )}
                </li>
              ))}
            </ul>
          </HoverCard.Content>
        </HoverCard>
      )}
    </span>
  )
}

export default BadgeList
